import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    closestSection.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
    this.#renderQuantityTotal(this.#getCurrentQuantity());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    closestSection.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
  }

  #handleQuantityUpdate = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const quantity = Number(event.detail?.quantity ?? 1);
    if (!Number.isFinite(quantity) || quantity < 1) return;

    this.#renderQuantityTotal(quantity);
  };

  #getCurrentQuantity() {
    const closestSection = this.closest('.shopify-section, dialog');
    const quantityInput = closestSection?.querySelector(
      'quantity-selector-component [ref="quantityInput"]:not([data-cart-line])'
    );

    return Number(quantityInput?.value) || 1;
  }

  #formatMoney(cents) {
    const currency = this.dataset.currencyCode || 'EUR';
    const locale = document.documentElement.lang || 'fr-FR';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).format(cents / 100);
  }

  #renderQuantityTotal(quantity) {
    const unitPrice = Number(this.dataset.unitPrice);
    if (!Number.isFinite(unitPrice)) return;

    const priceContainer = this.querySelector('[ref="priceContainer"]');
    if (!priceContainer) return;

    const totalPrice = this.#formatMoney(unitPrice * quantity);
    const priceValue = priceContainer.querySelector('.price bdi');
    if (priceValue) priceValue.textContent = totalPrice;

    const compareAt = Number(this.dataset.unitCompareAtPrice || 0);
    const compareAtValue = priceContainer.querySelector('.compare-at-price bdi');
    if (compareAtValue && compareAt > unitPrice) {
      compareAtValue.textContent = this.#formatMoney(compareAt * quantity);
    }

    const closestSection = this.closest('.shopify-section, dialog');
    const stickyPriceValue = closestSection?.querySelector('.sticky-add-to-cart__price .price bdi');
    if (stickyPriceValue) stickyPriceValue.textContent = totalPrice;

    const stickyCompareAtValue = closestSection?.querySelector('.sticky-add-to-cart__price .compare-at-price bdi');
    if (stickyCompareAtValue && compareAt > unitPrice) {
      stickyCompareAtValue.textContent = this.#formatMoney(compareAt * quantity);
    }

    this.dataset.currentQuantity = String(quantity);
  }

  /**
   * Handles product select event and updates the price.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const { priceContainer, volumePricingNote } = this.refs;
        // Find the new product-price element in the updated HTML
        const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
        if (!newProductPrice) return;

        this.dataset.unitPrice = newProductPrice.dataset.unitPrice ?? this.dataset.unitPrice;
        this.dataset.unitCompareAtPrice = newProductPrice.dataset.unitCompareAtPrice ?? '0';
        this.dataset.currencyCode = newProductPrice.dataset.currencyCode ?? this.dataset.currencyCode;

        // Update price container
        const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
        if (newPrice && priceContainer) {
          priceContainer.replaceWith(newPrice);
        }

        // Update volume pricing note
        const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          // Use newPrice since priceContainer was just replaced and now points to the detached element
          newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
        } else {
          volumePricingNote.replaceWith(newNote);
        }

        // Update installments (SPI banner) variant ID to trigger payment terms re-render
        const installmentsInput = /** @type {HTMLInputElement|null} */ (
          this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
        );
        if (installmentsInput) {
          installmentsInput.value = detail.resource?.id ?? '';
          installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        this.#renderQuantityTotal(this.#getCurrentQuantity());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
